/**
 * Europe/London-aware time helpers for the On Time Tracker skill.
 *
 * Vercel runs functions in UTC, but engineers live and work in UK local
 * time. Using `getUTCDay`/`getUTCHours` on a UTC clock skips the first
 * hour of every working day in BST, and `setHours(0,0,0,0)` in dashboard
 * code returns midnight UTC = 01:00 BST = "yesterday" before 01:00 BST
 * and "today" thereafter. Centralise the TZ logic here so the cron, the
 * dashboard, and the status builder always agree on "today" / "now".
 *
 * Phase 4 will read the engineer's actual timezone from the account; for
 * now everyone is UK.
 */

const LONDON_TZ = 'Europe/London';

/**
 * Return the parts of `now` rendered in London local time. Uses
 * Intl.DateTimeFormat so BST / GMT switches are handled by the platform's
 * tz database — no hand-coded offsets, no DST surprises.
 */
function londonParts(now: Date): {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
} {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hour12: false,
  }).formatToParts(now);

  const get = (type: string): string => parts.find(p => p.type === type)?.value ?? '';
  const weekdayMap: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
  };

  // 'hour' formatted as 24-hour with hour12: false can be '24' at midnight in some locales;
  // normalise to 0.
  const hourStr = get('hour');
  const hour = hourStr === '24' ? 0 : Number(hourStr);

  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour,
    minute: Number(get('minute')),
    weekday: weekdayMap[get('weekday')] ?? 0,
  };
}

/** Weekday (0=Sun..6=Sat) in London local time. */
export function londonWeekday(now: Date): number {
  return londonParts(now).weekday;
}

/** Minutes since midnight in London local time (0..1439). */
export function londonMinuteOfDay(now: Date): number {
  const p = londonParts(now);
  return p.hour * 60 + p.minute;
}

/**
 * Hour (0..23) in London local time — DST-correct via the tz database.
 * Used by the UTC-scheduled batch crons to fire at an exact UK-local hour:
 * schedule the cron at both candidate UTC hours and gate on `londonHour`.
 */
export function londonHour(now: Date): number {
  return londonParts(now).hour;
}

/** "YYYY-MM-DD" for the current London-local day. */
export function londonTodayIso(now: Date): string {
  const p = londonParts(now);
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/**
 * The London calendar day an INSTANT falls on.
 *
 * Same arithmetic as `londonTodayIso` — which has always accepted any Date —
 * under a name that does not lie at the call site. "What day was this payment
 * taken on" is not a question about today, and `londonTodayIso(payment.paidAt)`
 * reads like a bug even when it is correct.
 *
 * 🚨 This is the fix for the OTHER half of anti-pattern #9, and the half that
 * mis-states things that already happened. `new Date(stored).toISOString()
 * .slice(0, 10)` gives the UTC calendar day of a stored timestamptz, so a
 * payment taken at 00:30 BST on 1 September is written to Xero, QuickBooks or
 * FreeAgent dated 31 August — the wrong MONTH in somebody's books, from an
 * expression that looks like plain formatting.
 */
export function londonDateOf(instant: Date): string {
  return londonTodayIso(instant);
}

/**
 * A Date representing 00:00 London-local on the day of `now`. Useful for
 * "jobs scheduled today" queries that compare against scheduled_for, which
 * we store as UTC ISO timestamps.
 */
export function londonStartOfDay(now: Date): Date {
  const iso = londonTodayIso(now);
  // Construct an instant for "iso T00:00 London time". The simplest robust
  // way: build a Date from the ISO string anchored to UTC, then subtract
  // the difference between that UTC instant and the same wall-clock time
  // in London. Two passes of Intl handle the BST/GMT split.
  const utcMidnight = new Date(`${iso}T00:00:00.000Z`);
  const renderedAsLondon = londonParts(utcMidnight);
  // Compose a UTC date matching the London wall-clock at midnight.
  const reconstructed = Date.UTC(
    renderedAsLondon.year,
    renderedAsLondon.month - 1,
    renderedAsLondon.day,
    renderedAsLondon.hour,
    renderedAsLondon.minute,
  );
  const offsetMs = reconstructed - utcMidnight.getTime();
  return new Date(utcMidnight.getTime() - offsetMs);
}

/**
 * Monday–Friday of the current London working week. Server runs UTC, so naive
 * `getDay()`/`getDate()` math picks last week between 00:00–01:00 UK in BST.
 * Returns both the UTC instants (Monday 00:00 → Friday 23:59:59.999 London,
 * for scheduled_start range queries) and the calendar date strings (for
 * date-column queries like project_days.scheduled_date).
 */
export function londonWorkWeek(now: Date): {
  mondayStart: Date;
  fridayEnd: Date;
  mondayDate: string;
  fridayDate: string;
} {
  const todayIso = londonTodayIso(now);
  const weekdayMon0 = (londonWeekday(now) + 6) % 7; // 0=Mon … 6=Sun
  // Anchor at noon UTC so day arithmetic never crosses a date boundary (DST is
  // ±1h, far from noon); the .slice(0,10) then reads the right calendar date.
  const todayNoon = new Date(`${todayIso}T12:00:00Z`);
  const mondayNoon = new Date(todayNoon);
  mondayNoon.setUTCDate(mondayNoon.getUTCDate() - weekdayMon0);
  const fridayNoon = new Date(mondayNoon);
  fridayNoon.setUTCDate(fridayNoon.getUTCDate() + 4);
  const saturdayNoon = new Date(mondayNoon);
  saturdayNoon.setUTCDate(saturdayNoon.getUTCDate() + 5);
  return {
    mondayStart: londonStartOfDay(mondayNoon),
    fridayEnd: new Date(londonStartOfDay(saturdayNoon).getTime() - 1),
    mondayDate: mondayNoon.toISOString().slice(0, 10),
    fridayDate: fridayNoon.toISOString().slice(0, 10),
  };
}

/**
 * Convert a naive local-time ISO string ("2026-05-20T09:00:00") to a true UTC
 * ISO string, interpreting the naive value as Europe/London wall-clock time.
 *
 * Vercel runs the engine in UTC. When the agent emits a naive scheduled_start
 * for "9am tomorrow", a plain `new Date(naive)` server-side reads it as UTC
 * and stores 09:00Z, which then displays as 10:00 BST in summer. Run all
 * agent-supplied schedule times through here before writing to the DB so the
 * stored UTC matches the user's intended London local time.
 *
 * If the input already carries timezone info (Z or ±HH:MM), it's returned
 * as-is — only naive strings get the London treatment.
 *
 * 🚨 That passthrough is right for a string WE built and wrong for one the
 * MODEL wrote, and this function cannot tell them apart. A caller holding a
 * model-supplied datetime must go through `readAgentDateTime` instead, which
 * refuses the zoned case rather than trusting it. See its docblock for the
 * call that moved a job an hour.
 */
export function londonNaiveToUtcIso(input: string | null | undefined): string {
  if (!input) return new Date().toISOString();
  // Already has timezone info — trust it.
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(input)) return new Date(input).toISOString();

  // Anchor the naive wall-clock to UTC, then ask Intl what that instant
  // would render as in London. The difference is the offset to subtract.
  const utcGuess = new Date(`${input}${input.includes('T') ? '' : 'T00:00:00'}Z`);
  if (isNaN(utcGuess.getTime())) return new Date().toISOString();
  const renderedAsLondon = londonParts(utcGuess);
  const reconstructed = Date.UTC(
    renderedAsLondon.year,
    renderedAsLondon.month - 1,
    renderedAsLondon.day,
    renderedAsLondon.hour,
    renderedAsLondon.minute,
  );
  const offsetMs = reconstructed - utcGuess.getTime();
  return new Date(utcGuess.getTime() - offsetMs).toISOString();
}

/**
 * An instant written as London wall-clock time with its offset —
 * "2026-10-06T08:00:00+01:00" for eight in the morning in BST.
 *
 * For any datetime a MODEL is asked to copy back. A UTC string labelled with
 * a London time ("8:00 am (slot_start: 2026-10-06T07:00:00.000Z)") shows the
 * model two clocks that disagree by an hour, and it "fixes" them: the
 * receptionist sent "2026-10-01T08:00:00" for an 8am slot listed as 07:00Z,
 * and the visit was pencilled in at nine. With the offset form the clock in
 * the string IS the clock the caller heard, so copying it, or dropping the
 * offset, both land on the right time.
 */
export function londonOffsetIso(instant: Date | string): string {
  const d = typeof instant === 'string' ? new Date(instant) : instant;
  const p = londonParts(d);
  const wallMs = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  const offsetMin = Math.round((wallMs - Math.floor(d.getTime() / 60_000) * 60_000) / 60_000);
  const sign = offsetMin < 0 ? '-' : '+';
  const abs = Math.abs(offsetMin);
  const two = (n: number) => String(n).padStart(2, '0');
  return `${p.year}-${two(p.month)}-${two(p.day)}T${two(p.hour)}:${two(p.minute)}:00`
    + `${sign}${two(Math.floor(abs / 60))}:${two(abs % 60)}`;
}

/** Does this string end in a timezone marker — `Z` or `±HH:MM`? */
export function carriesTimezone(input: string): boolean {
  return /Z$|[+-]\d{2}:?\d{2}$/.test(input.trim());
}

/**
 * Read a datetime the MODEL wrote, for storing against a record.
 *
 * ── Why this is not just `londonNaiveToUtcIso` ────────────────────────────
 *
 * That helper trusts a timezone marker when it finds one — correct for the
 * twenty-odd call sites that build their own string from a date and an HH:MM,
 * and wrong for the only sources that ever actually carry one: the model.
 *
 * 10 September 2026, 20:16, a heating customer. The engineer says *"move it to
 * 3:00"* and the model calls
 * `update_job({ scheduled_start: "2026-09-10T15:00:00.000Z" })`. It has written
 * the wall clock he said and appended `Z` as punctuation — it is not asserting
 * UTC, it is formatting. The marker was trusted, 15:00Z stored, and London in
 * September is BST, so the job moved to **four o'clock**. The tool's own result
 * echoed `"time":"16:00"` in the same breath as Mucka saying "3 o'clock", and
 * nothing anywhere disagreed with anything.
 *
 * ── Why it refuses rather than stripping the marker ───────────────────────
 *
 * Stripping assumes the model wrote the wall clock and decorated it. That is
 * what happened in the one instance anyone has seen — and one instance is not
 * enough to bet an hour of a customer's day on, because the opposite habit
 * (converting properly to UTC, then marking it) produces an identical string
 * and stripping it would shift that one an hour the other way. Two readings,
 * no way to tell them apart from the string, so this takes neither: it says
 * what it cannot resolve and asks for the London wall clock, which costs one
 * round trip and cannot be wrong. Same discipline as `create_job` refusing a
 * type nobody named (#2741) rather than guessing a plausible one.
 *
 * ── How often ─────────────────────────────────────────────────────────────
 *
 * Measured over every model-supplied datetime argument stored in `ai_messages`:
 * **1 of 84 on 10–11 September, 0 of 375 over 14–21 September**, all accounts
 * and all channels. So it is rare, and the second window is not evidence it
 * has stopped — at the low end of the first window's interval, zero in 375 is
 * exactly what rarity looks like. The same detector was run over the day of the
 * incident first and found it, which is the only reason the zero means anything
 * at all (CLAUDE.md #22).
 */
export function readAgentDateTime(
  input: string,
): { ok: true; iso: string } | { ok: false; error: string } {
  const raw = input.trim();
  if (carriesTimezone(raw)) {
    return {
      ok: false,
      error:
        'NOT DONE — nothing was changed. That datetime carries a timezone '
        + `("${raw}"), and I cannot tell whether you converted it or just wrote the `
        + 'time they said and marked it. Send the time the way they said it, with no '
        + 'Z and no offset — "2026-03-13T15:00:00" for three in the afternoon. '
        + 'Everything here is Europe/London.',
    };
  }
  return { ok: true, iso: londonNaiveToUtcIso(raw) };
}

/**
 * The last COMPLETE London calendar week — Monday 00:00 to Sunday 23:59:59.999
 * — relative to `now`, plus the four complete weeks before it.
 *
 * 🚨 This exists because `londonWorkWeek` answers a different question and one
 * caller asked it the wrong one. A rule that compares "this week" against a
 * rolling average of finished weeks is comparing a week that is HOURS old
 * against weeks that are seven days old: the engine runs at 07:00, so on a
 * Monday the current week holds whatever happened between midnight and dawn,
 * which is nothing. `revenue_dip` shipped that way and said "you've had no
 * revenue this week" on 113 of its 125 production cards, every Monday one at
 * exactly 100% below average, and was approved zero times in six months.
 * Comparing a period to its own peers means waiting until it IS one.
 *
 * Full Monday–Sunday on purpose, not Monday–Friday: a five-day numerator
 * against a seven-day baseline drops every weekend invoice out of the current
 * period while still counting it in the average (14.8% of paid-invoice value
 * on production is raised at a weekend), which is a dip the rule manufactures
 * out of the calendar.
 */
/**
 * How many complete weeks a week is compared against. Also the history an
 * account must have before the comparison is allowed to run at all.
 */
const PRIOR_WEEKS = 12;

export function londonLastCompleteWeek(now: Date): {
  weekStart: Date;
  weekEnd: Date;
  weekStartDate: string;
  weekEndDate: string;
  priorStart: Date;
  priorWeeks: number;
  /**
   * The date an account's history must reach back to before a
   * week-against-its-peers comparison means anything — `priorStart` as a
   * London calendar date, for comparing against a first-ever record.
   */
  priorStartDate: string;
} {
  // Monday of the week `now` falls in — the week still running.
  const { mondayStart: currentMonday } = londonWorkWeek(now);
  // Noon anchoring for the same reason londonWorkWeek does it: DST is ±1h and
  // never near noon, so day arithmetic cannot slip a calendar date.
  const currentMondayNoon = new Date(`${londonDateOf(currentMonday)}T12:00:00Z`);

  const startNoon = new Date(currentMondayNoon);
  startNoon.setUTCDate(startNoon.getUTCDate() - 7);
  const endExclusiveNoon = new Date(currentMondayNoon);
  const priorStartNoon = new Date(currentMondayNoon);
  // 12 complete weeks, not 4. "Three months of data to know what a good
  // average is" — a month of onboarding is not an average, and one good
  // fortnight in four weeks sets the bar high enough to make the next
  // ordinary week look like a collapse.
  priorStartNoon.setUTCDate(priorStartNoon.getUTCDate() - 7 - PRIOR_WEEKS * 7);
  const sundayNoon = new Date(startNoon);
  sundayNoon.setUTCDate(sundayNoon.getUTCDate() + 6);

  return {
    weekStart: londonStartOfDay(startNoon),
    // The instant before the current week opens, so the two windows abut
    // without overlapping.
    weekEnd: new Date(londonStartOfDay(endExclusiveNoon).getTime() - 1),
    weekStartDate: londonDateOf(londonStartOfDay(startNoon)),
    weekEndDate: londonDateOf(londonStartOfDay(sundayNoon)),
    priorStart: londonStartOfDay(priorStartNoon),
    priorWeeks: PRIOR_WEEKS,
    priorStartDate: londonDateOf(londonStartOfDay(priorStartNoon)),
  };
}
